import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FilterBar from "./components/FilterBar";
import MovieCard from "@/components/feature/MovieCard";
import Pagination from "@/components/feature/Pagination";
import useSeo from "@/hooks/useSeo";
import useFavorites from "@/hooks/useFavorites";
import { allMovies } from "@/utils/data";

const PAGE_SIZE = 8;

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { favorites } = useFavorites();

  const type = searchParams.get("type") || "全部";
  const region = searchParams.get("region") || "全部";
  const year = searchParams.get("year") || "全部";
  const rating = searchParams.get("rating") || "全部";
  const sort = searchParams.get("sort") || "popular";
  const favOnly = searchParams.get("fav") === "1";
  const pageParam = Number.parseInt(searchParams.get("page") || "1", 10);

  const update = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === "全部" || !value) {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    if (key !== "page") next.delete("page");
    setSearchParams(next);
  };

  const reset = () => {
    const next = new URLSearchParams();
    if (favOnly) next.set("fav", "1");
    setSearchParams(next);
  };

  const filtered = useMemo(() => {
    const base = favOnly
      ? allMovies.filter((m) => favorites.includes(m.id))
      : allMovies;
    return base.filter((m) => {
      const typeOk = type === "全部" || m.type === type;
      const regionOk = region === "全部" || m.region === region;
      const yearOk = year === "全部" || String(m.year) === year;
      const ratingOk = rating === "全部" || m.rating >= Number.parseFloat(rating);
      return typeOk && regionOk && yearOk && ratingOk;
    });
  }, [favOnly, favorites, type, region, year, rating]);

  const sorted = useMemo(() => {
    const list = [...filtered];
    if (sort === "latest") {
      list.sort((a, b) => b.year - a.year || b.rating - a.rating);
    } else if (sort === "rating") {
      list.sort((a, b) => b.rating - a.rating);
    }
    return list;
  }, [filtered, sort]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const current = Math.min(Math.max(1, pageParam || 1), totalPages);
  const paged = sorted.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const heading = favOnly
    ? "我的收藏"
    : type === "电影"
      ? "百合电影"
      : type === "电视剧"
        ? "百合电视剧"
        : "影视分类";

  useSeo({
    title: `${heading} - 百合影视分类与筛选`,
    description: `百合影视分类页，支持按类型、年代、地区与评分多维度筛选，当前共收录 ${sorted.length} 部百合题材影视作品，帮你快速找到想看的那一部。`,
    keywords: `百合影视分类,百合筛选,${
      type === "全部" ? "百合电影,百合电视剧" : `百合${type}`
    },百合推荐`,
  });

  return (
    <div className="pt-24 md:pt-32">
      <div className="border-b border-background-200/60 bg-secondary-50">
        <div className="mx-auto max-w-[1280px] px-4 py-10 md:px-6 md:py-14">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "影视分类", to: favOnly ? undefined : "/category" },
              ...(favOnly ? [{ label: "我的收藏" }] : []),
              ...(!favOnly && type !== "全部" ? [{ label: type }] : []),
            ]}
          />
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
            {heading}
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            {favOnly
              ? "这里汇集了你收藏过的百合影视作品，随时回到你心动的那一刻。"
              : "从治愈日常到都市甜剧，用多维筛选找到最对味的百合影视作品。支持按类型、年代、地区与评分区间精准查询。"}
          </p>
          <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-background-50 px-4 py-1.5 text-xs text-foreground-600">
            <span className="flex h-4 w-4 items-center justify-center text-primary-600">
              <i className="ri-database-2-line" />
            </span>
            共找到 <strong className="text-foreground-950">{sorted.length}</strong> 部作品
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
        <FilterBar
          type={type}
          region={region}
          year={year}
          rating={rating}
          sort={sort}
          onChange={update}
          onReset={reset}
          onSortChange={(v) => update("sort", v)}
        />

        {paged.length > 0 ? (
          <>
            <div className="mt-8 grid grid-cols-2 gap-x-5 gap-y-8 sm:grid-cols-3 lg:grid-cols-4">
              {paged.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
            <Pagination
              current={current}
              totalPages={totalPages}
              onChange={(p) => {
                update("page", String(p));
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
            />
          </>
        ) : (
          <div className="mt-10 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300/70 bg-background-50 px-6 py-20 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 text-3xl text-foreground-400">
              <i className="ri-emotion-sad-line" />
            </span>
            <h2 className="mt-5 font-heading text-lg font-semibold text-foreground-950">
              没有找到符合条件的作品
            </h2>
            <p className="mt-2 max-w-md text-sm text-foreground-600">
              {favOnly
                ? "你还没有收藏任何作品，先去逛逛，把喜欢的作品收藏起来吧。"
                : "试试放宽筛选条件，或者重置筛选，浏览全部百合影视作品。"}
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              <button
                type="button"
                onClick={reset}
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-refresh-line" />
                </span>
                重置筛选
              </button>
              <Link
                to="/category"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300/70 px-5 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                浏览全部作品
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}